import { useCallback, useEffect } from "react";

import { PAYMENT_ACTIVITY_ROUTE } from "../../../components/PaymentActivity/routes";
import usePathname from "../../../components/hooks/usePathname";

import { useSidebarContainer } from "./sidebar.hooks";

import type {
  NAFOptionItem,
  SidebarOptionItem,
  SidebarRootItem,
  StandardOptionItem,
} from "./sidebar.types";

const PAYMENT_ACTIVITY_ROUTE_PREFIX = `/${PAYMENT_ACTIVITY_ROUTE}`;
const BULK_PAYMENT_NEW_PREFIX = `${PAYMENT_ACTIVITY_ROUTE_PREFIX}/bulk/new`;
const FILE_IMPORT_UPLOAD_PREFIX = `${PAYMENT_ACTIVITY_ROUTE_PREFIX}/upload`;

const NAF_ROUTE_PREFIX = "/apps/";

// ---------------------------------------------------------------------------
// Route matchers — pure functions, no React state
// ---------------------------------------------------------------------------

/**
 * Returns true when `pathname` starts with `route`.
 *
 * Special case: the payment-activity route is NOT considered active
 * when the user is on a bulk-payment sub-route (/bulk/new, /bulk/upload)
 * because those render in a different view.
 */
export const isSubItemRouteActive = (
  route: string | undefined,
  pathname?: string
): boolean => {
  if (!route) return false;
  const currentPath = pathname ?? window.location.pathname;

  if (
    route === PAYMENT_ACTIVITY_ROUTE_PREFIX &&
    (currentPath.startsWith(BULK_PAYMENT_NEW_PREFIX) ||
      currentPath.startsWith(FILE_IMPORT_UPLOAD_PREFIX))
  ) {
    return false;
  }

  return currentPath.startsWith(route);
};

/**
 * Finds the longest matching standard sub-item route.
 * Longest-match wins so /transfer/loans beats /transfer.
 */
export const getActiveSubItemRoute = (
  menuOptions: SidebarOptionItem[] | undefined,
  pathname?: string
): StandardOptionItem["route"] | undefined => {
  if (!menuOptions) return undefined;

  return menuOptions
    .filter(
      (option): option is StandardOptionItem & { route: string } =>
        option.type === "standard" && Boolean(option.route)
    )
    .sort((a, b) => b.route.length - a.route.length)
    .find((option) => isSubItemRouteActive(option.route, pathname))?.route;
};

/**
 * Finds the active NAF sub-item's client_id within a menu group.
 * Returns undefined if no NAF sub-item matches.
 */
export const getActiveNAFClientId = (
  menuOptions: SidebarOptionItem[] | undefined,
  pathname?: string
): string | undefined => {
  if (!menuOptions) return undefined;

  const getClientId = (option: NAFOptionItem): string | undefined =>
    option.nafOption.discriminator === "app"
      ? option.nafOption.app.client_id
      : undefined;

  return menuOptions
    .filter((option): option is NAFOptionItem => option.type === "naf")
    .map(getClientId)
    .find((clientId) => isNAFAppRouteActive(clientId, pathname));
};

/**
 * Returns true when `pathname` starts with /apps/<clientId>.
 */
const isNAFAppRouteActive = (
  clientId: string | undefined,
  pathname?: string
): boolean => {
  if (!clientId) return false;
  const currentPath = pathname ?? window.location.pathname;
  return currentPath.startsWith(`${NAF_ROUTE_PREFIX}${clientId}`);
};

// ---------------------------------------------------------------------------
// Header active-state helpers + highlighting hook
// ---------------------------------------------------------------------------

export interface MenuItemState {
  /** Whether the header row should show the active style. */
  isActive: boolean;
  /** Whether the accordion is expanded. */
  isOpen: boolean;
  /** Toggle the accordion open/closed. */
  onUserToggle: () => void;
}

/**
 * Returns true if any sub-item (standard or NAF) within a menu
 * group, or the accounts detail route, matches the current pathname.
 */
const hasActiveChild = (menuItem: SidebarRootItem, pathname: string): boolean =>
  Boolean(getActiveSubItemRoute(menuItem.menuOptions, pathname)) ||
  Boolean(getActiveNAFClientId(menuItem.menuOptions, pathname)) ||
  (menuItem.key === "accounts" && pathname.startsWith("/accounts/"));

/**
 * Checks if the section header's own route matches the pathname.
 * Handles special cases where dashboard and accounts share "/" / "/dashboard".
 */
const isRootHeaderRouteActive = (
  route: string | undefined,
  key: string | undefined,
  pathname: string
): boolean => {
  if (!route) return false;
  if (route === "/dashboard" && key === "accounts") return false;
  if (pathname === "/" && key === "dashboard") return true;
  return pathname.includes(route);
};

/**
 * Returns the effective route for a menu item's header.
 * Top-level NAF items don't have a `route` but navigate to /apps/<client_id>.
 */
const getHeaderRoute = (menuItem: SidebarRootItem): string | undefined =>
  menuItem.route ??
  (menuItem.nafApp?.client_id
    ? `${NAF_ROUTE_PREFIX}${menuItem.nafApp.client_id}`
    : undefined);

/**
 * Determines whether a menu item's header should show active styling.
 *
 * Two cases:
 * 1. Accordion with active child — only active when sidebar is collapsed
 *    (when expanded, the child sub-item shows active instead)
 * 2. Header-only item (including top-level NAF) — active when its
 *    route matches the current pathname
 */
const shouldShowHeaderActive = (
  menuItem: SidebarRootItem,
  pathname: string,
  isSidebarCollapsed: boolean
): boolean => {
  if (hasActiveChild(menuItem, pathname)) {
    return isSidebarCollapsed;
  }

  return isRootHeaderRouteActive(
    getHeaderRoute(menuItem),
    menuItem.key,
    pathname
  );
};

/**
 * Manages sidebar menu state: which header is active, which sections
 * are expanded, and auto-expands sections on mount when landing on
 * a sub-item route.
 *
 * Sub-item-level active state (which specific sub-item is highlighted)
 * is handled by useMenuItemOptions via getActiveSubItemRoute / getActiveNAFClientId.
 */
export const useSidebarRouteHighlighting = (menuItems: SidebarRootItem[]) => {
  const pathname = usePathname();
  const { isCollapsed, isMenuGroupExpanded, toggleMenuGroup } =
    useSidebarContainer();

  // Auto-expand sections on mount when a child route is active
  useEffect(() => {
    menuItems.forEach((menuItem) => {
      const key = menuItem.key || "";
      if (isMenuGroupExpanded(key)) return;

      if (hasActiveChild(menuItem, pathname)) {
        toggleMenuGroup(key);
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getMenuHighlightState = useCallback(
    (menuItem: SidebarRootItem): MenuItemState => {
      const key = menuItem.key || "";
      return {
        isActive: shouldShowHeaderActive(menuItem, pathname, isCollapsed),
        isOpen: isMenuGroupExpanded(key),
        onUserToggle() {
          toggleMenuGroup(key);
        },
      };
    },
    [isCollapsed, isMenuGroupExpanded, toggleMenuGroup, pathname]
  );

  return { getMenuHighlightState };
};
